import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';
import { sendEmail } from './emails/config';
import { EMAIL_CONFIG } from './emails/config';
import {
  getWelcomeEmailTemplate,
  getPasswordResetEmailTemplate,
} from './emails/templates';
import { emailTemplates } from './emails/templates';
import { resendService } from './resendService';

export const authService = {
  // Authentication Methods
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  },

  async signUp(
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) {
    try {
      // Sign up the user
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
          },
          emailRedirectTo: `${window.location.origin}/?mode=dashboard`,
        },
      });

      if (error) throw error;
      if (!data.user) throw new Error('Failed to create user');

      // Initialize trial subscription with 7-day expiry
      const trialEndsAt = new Date();
      trialEndsAt.setDate(trialEndsAt.getDate() + 7);

      // Create subscription record using the admin API endpoint
      const response = await fetch(
        'https://pjhckyctpaxkboxchiyj.supabase.co/functions/v1/create-trial-subscription',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            userId: data.user.id,
            trialEndsAt: trialEndsAt.toISOString()
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('Error creating subscription:', error);
        throw new Error('Failed to create trial subscription');
      }

      // Send welcome email
      await resendService.sendWelcomeEmail(email, firstName);

      return data;
    } catch (error) {
      console.error('Error in signUp:', error);
      throw error;
    }
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    // Clear all auth-related storage
    localStorage.removeItem('auth-storage');
    localStorage.removeItem('sb-auth-token');
    localStorage.removeItem('supabase.auth.token');
  },

  // Guest Access
  async guestAccess(projectPassword: string) {
    try {
      // First verify the project exists
      const { count, error: countError } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .eq('guest_password', projectPassword);

      if (countError) throw countError;
      
      // If we found a match, get the full data
      if (count && count > 0) {
        const { data, error } = await supabase
          .from('projects')
          .select()
          .eq('guest_password', projectPassword)
          .limit(1)
          .single();

        if (error) throw error;
        return data;
      }

      throw new Error('Invalid project password. Please check and try again.');
    } catch (error) {
      console.error('Guest access error:', error);
      throw error;
    }
  },

  async changePassword(currentPassword: string, newPassword: string) {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      // Send password changed notification
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        await resendService.send({
          to: user.email,
          ...emailTemplates.passwordChanged(user.email)
        });
      }
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }
};